import './verificationModule.css';
import type { VerificationModuleViewModel } from '@blackmarble/sim-core';

interface VerificationModuleOptions {
  dismissText: string;
  reduceMotion: boolean;
}

interface ActiveModule {
  elapsed: number;
  resolveSec: number;
  lingerSec: number;
}

const RESOLVE_SEC = 0.45;
const LINGER_SEC = 3.25;

export class VerificationModule {
  private readonly root: HTMLDivElement;
  private readonly shell: HTMLDivElement;
  private readonly carvedEl: HTMLDivElement;
  private readonly archiveNameEl: HTMLDivElement;
  private readonly archiveStatusEl: HTMLDivElement;
  private readonly archiveExcerptEl: HTMLDivElement;
  private readonly stampEl: HTMLDivElement;
  private readonly dismissEl: HTMLDivElement;
  private active: ActiveModule | null = null;

  constructor() {
    this.root = document.createElement('div');
    this.root.id = 'verification-module';
    this.root.innerHTML = `
      <div class="vm-shell vm-pending">
        <div class="vm-column vm-left">
          <div class="vm-label">Carved Name</div>
          <div class="vm-carved"></div>
        </div>
        <div class="vm-bridge">
          <div class="vm-stamp"></div>
          <div class="vm-line" aria-hidden="true">
            <span class="vm-seg vm-seg-left"></span>
            <span class="vm-seg vm-seg-right"></span>
          </div>
          <div class="vm-dismiss"></div>
        </div>
        <div class="vm-column vm-right">
          <div class="vm-label">Archive Record</div>
          <div class="vm-register">
            <div class="vm-row"><span class="vm-key">NAME</span><span class="vm-value vm-archive-name"></span></div>
            <div class="vm-row"><span class="vm-key">STATUS</span><span class="vm-value vm-archive-status"></span></div>
            <div class="vm-excerpt hidden"></div>
          </div>
        </div>
      </div>`;
    this.root.style.display = 'none';
    document.body.appendChild(this.root);
    this.shell = this.root.querySelector('.vm-shell') as HTMLDivElement;
    this.carvedEl = this.root.querySelector('.vm-carved') as HTMLDivElement;
    this.archiveNameEl = this.root.querySelector('.vm-archive-name') as HTMLDivElement;
    this.archiveStatusEl = this.root.querySelector('.vm-archive-status') as HTMLDivElement;
    this.archiveExcerptEl = this.root.querySelector('.vm-excerpt') as HTMLDivElement;
    this.stampEl = this.root.querySelector('.vm-stamp') as HTMLDivElement;
    this.dismissEl = this.root.querySelector('.vm-dismiss') as HTMLDivElement;
  }

  get visible(): boolean {
    return this.active !== null;
  }

  show(view: VerificationModuleViewModel, options: VerificationModuleOptions): void {
    this.populate(view, options.dismissText);
    this.root.style.display = 'flex';
    this.shell.className = 'vm-shell vm-pending';
    this.shell.dataset.outcome = view.outcome.toLowerCase();
    this.active = {
      elapsed: 0,
      resolveSec: options.reduceMotion ? 0 : RESOLVE_SEC,
      lingerSec: options.reduceMotion ? 2.4 : LINGER_SEC,
    };
    if (options.reduceMotion) this.resolve(view);
  }

  tick(dt: number): void {
    if (!this.active) return;
    this.active.elapsed += dt;
    if (this.active.elapsed >= this.active.resolveSec) this.resolveCurrent();
    if (this.active.elapsed >= this.active.lingerSec) this.dismiss();
  }

  dismiss(): void {
    if (!this.active) return;
    this.active = null;
    this.root.style.display = 'none';
    this.shell.className = 'vm-shell vm-pending';
  }

  dispose(): void {
    this.dismiss();
    this.root.remove();
  }

  private populate(view: VerificationModuleViewModel, dismissText: string): void {
    this.carvedEl.textContent = view.carvedName;
    this.archiveNameEl.textContent = view.archiveName;
    this.archiveStatusEl.textContent = view.archiveStatus.replace('STATUS: ', '');
    this.stampEl.textContent = view.stamp;
    this.dismissEl.textContent = dismissText;
    if (!view.archiveExcerpt) {
      this.archiveExcerptEl.textContent = '';
      this.archiveExcerptEl.className = 'vm-excerpt hidden';
      return;
    }
    this.archiveExcerptEl.textContent = view.archiveExcerpt;
    this.archiveExcerptEl.className = 'vm-excerpt';
  }

  private resolveCurrent(): void {
    if (!this.active || !this.shell.dataset.outcome || !this.shell.classList.contains('vm-pending')) return;
    this.resolve({
      outcome: this.shell.dataset.outcome === 'contradiction' ? 'CONTRADICTION' : 'TRUTH',
      lineState: this.shell.dataset.outcome === 'contradiction' ? 'sever' : 'hold',
    });
  }

  private resolve(view: Pick<VerificationModuleViewModel, 'outcome' | 'lineState'>): void {
    this.shell.className = `vm-shell vm-resolved vm-${view.outcome.toLowerCase()} vm-${view.lineState}`;
  }
}
